import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { FadeUp, MaskLineScroll } from "../components/Reveal";
import { BRAND } from "../lib/brand";
import { api } from "../lib/api";

const FILTERS = [
  "All", "JDM", "Collision", "Insurance", "Custom Paint", "Color Change",
  "Restoration", "Drift", "Race / Track", "Personal Builds", "Specialty",
];

export default function Gallery() {
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState("All");

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data || [])).catch(() => setProjects([]));
  }, []);

  const empty = projects.length === 0;

  return (
    <>
      <SEO
        title="Gallery — ProForm Paint & Body Archive"
        description="The ProForm archive is being built. Follow @_pro_form_ for current work while collision repairs, custom paint, JDM builds, drift cars, and personal projects are organized."
        canonical={`https://${BRAND.domain}/gallery`}
      />

      <section className="pt-32 md:pt-40 pb-12 border-b border-ink-700">
        <div className="wrap grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="label mb-3">— Archive · 001</div>
            <h1 className="font-display uppercase text-5xl md:text-8xl leading-[0.88] tracking-tight">
              <MaskLineScroll>The ProForm archive</MaskLineScroll>
              <MaskLineScroll delay={0.08}>is being <span className="text-proform-red">built</span>.</MaskLineScroll>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-chrome-300 leading-relaxed">
              Years of collision repairs, custom finishes, imported builds, drift cars, race cars, and personal projects are being organized for the new ProForm gallery. New projects will be added here as photography and video are prepared.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={BRAND.instagramUrl} target="_blank" rel="noreferrer" className="btn btn-primary">Follow @{BRAND.instagramHandle} →</a>
              <Link to="/estimate" className="btn">Request an Estimate →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Disabled filters — future ready */}
      <section className="py-6 border-b border-ink-700 bg-ink-900">
        <div className="wrap flex flex-wrap items-center gap-2">
          <span className="label mr-3">Categories</span>
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => !empty && setActive(f)}
              disabled={empty}
              className={`font-mono text-[11px] tracking-[0.24em] uppercase px-3 py-2 border transition-colors ${
                active === f
                  ? "border-proform-red text-proform-red"
                  : "border-ink-700 text-chrome-300 hover:border-chrome-300"
              } ${empty ? "opacity-40 cursor-not-allowed" : ""}`}
              title={empty ? "Filters activate once projects are published" : undefined}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      {empty ? (
        <section className="py-24 md:py-32">
          <div className="wrap grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <FadeUp key={i} delay={i * 0.04}>
                <div className="relative aspect-[3/4] border border-ink-700 bg-ink-900 overflow-hidden flex items-end p-4">
                  <div>
                    <div className="label">— Slot · 0{i + 1}</div>
                    <div className="font-display text-2xl mt-2 text-chrome-300">Awaiting Project</div>
                  </div>
                  {/* diagonal lines */}
                  <div className="absolute inset-0 opacity-20 pointer-events-none"
                       style={{ backgroundImage: "repeating-linear-gradient(45deg, #1B1D20 0 1px, transparent 1px 22px)" }} />
                </div>
              </FadeUp>
            ))}
          </div>
          <div className="wrap mt-12">
            <div className="label">Reserved layout — first published projects will occupy these slots.</div>
          </div>
        </section>
      ) : (
        <section className="py-16 md:py-24">
          <div className="wrap grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {projects
              .filter((p) => active === "All" || p.category?.toLowerCase().includes(active.toLowerCase()))
              .map((p, i) => (
                <FadeUp key={p.id} delay={i * 0.04}>
                  <Link to={`/gallery/${p.slug}`} className="block clip-frame aspect-[4/5] card">
                    <img src={p.featured_image} alt={p.title} loading="lazy" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                      <div className="label">{p.category}</div>
                      <div className="font-display uppercase text-xl mt-1">{p.title}</div>
                    </div>
                  </Link>
                </FadeUp>
              ))}
          </div>
        </section>
      )}
    </>
  );
}
